/**
 * VoiceCandidateReview.jsx
 *
 * Hear three voices for a character and choose one.
 *
 * ⛔ WHY THIS EXISTS. `autoCast` designs and commits in one movement, keeping the first of the three
 * candidates ElevenLabs returns and discarding the other two — which throws away the operator's
 * actual workflow: *"I review the ElevenLabs 3 samples, choose one or resample."*
 *
 * ⭐ AND THE ECONOMICS WANT IT THAT WAY. A design call is cheap; the OPERATIONS land on
 * `createVoiceFromPreview` — 2 per voice, against 1,040 a month that cannot be bought. So all three
 * auditions are already paid for, rejecting two costs nothing, and the only spend is the one click
 * that casts a voice. Under the old flow, disliking a voice cost another 2 operations to re-roll.
 *
 * ⚠️ THE COST IS ON THE BUTTON, NOT IN A DIALOG. The operator has just listened — a confirmation
 * step on every choice would be friction on the one action this screen exists for. But 2 operations
 * is real, so the button says so and the result line reports what was spent. A rationed resource
 * should be legible without being nagged about.
 *
 * ⛔ ONE SOUND AT A TIME, through a single shared Audio object. Three players per character across a
 * 40-character queue is 120 ways to end up with two voices talking over each other, which is the one
 * thing that makes auditioning impossible.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Header, Icon, Label, Loader, Message, Popup, Segment } from 'semantic-ui-react';
import { apiClient } from '../../apiTools/apiClient';
import ConfirmModal from './ConfirmModal';

/** ⛔ 2 per voice — an ADD and an EDIT. Stated wherever the spend is offered. */
const OPS_PER_COMMIT = 2;

const fmtSecs = (s) => (s == null ? null : `${Number(s).toFixed(1)}s`);

const VoiceCandidateReview = ({
  litGroupUuid,
  onCast,
  /** True while a generation run is in flight — an empty queue then means "wait", not "nothing". */
  runActive = false,
  runMessage = null,
  /** Bumped by the parent on CASTING_COMPLETE, which is when there is certainly something new. */
  refreshNonce = 0,
  /** Tells the parent to watch a run this panel started, so Resample gets the same progress strip. */
  onRunStarted = null,
}) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [busyUuid, setBusyUuid] = useState(null);
  const [playingUuid, setPlayingUuid] = useState(null);
  const [notes, setNotes] = useState({});          // characterUuid → a sentence about what happened
  const [openPrompt, setOpenPrompt] = useState({}); // characterUuid → show the recipe

  /**
   * ⭐ ONE Audio, reused. Created lazily so a browser that blocks autoplay construction does not
   * break the first render, and torn down on unmount so leaving the tab stops the sound.
   */
  /**
   * ⭐ TAKE THE AUDIO AWAY, UNDER A NAME. "Take 2" means nothing once the file leaves this page,
   * and three takes for sixty characters is a hundred and eighty files.
   *
   * ⚠️ A plain anchor click rather than a fetch: the preview URL is already a presigned S3 GET,
   * and re-fetching it through JS would only add a blob copy and a CORS surface. `download` sets
   * the saved filename; the browser keeps the extension from the content type.
   */
  const downloadTake = useCallback((character, cand, i) => {
    if (!cand.previewUrl) return;
    const a = document.createElement('a');
    a.href = cand.previewUrl;
    a.download = `${character.characterName} - take ${i + 1}.mp3`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  }, []);

  const audioRef = useRef(null);
  const getAudio = useCallback(() => {
    if (!audioRef.current) audioRef.current = new Audio();
    return audioRef.current;
  }, []);
  useEffect(() => () => {
    if (audioRef.current) { audioRef.current.pause(); audioRef.current.src = ''; }
  }, []);

  const load = useCallback(async () => {
    if (!litGroupUuid) return;
    setLoading(true);
    setError(null);
    try {
      const r = await apiClient.get(`/litgroups/${litGroupUuid}/casting/candidates`);
      setData(r.data || null);
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Could not load the auditions');
    } finally {
      setLoading(false);
    }
  }, [litGroupUuid]);

  // ⭐ `refreshNonce` is in the deps on purpose: a finished run is the one moment the queue is
  // certainly out of date, and the operator should not have to find the Refresh button to see it.
  useEffect(() => { load(); }, [load, refreshNonce]);

  const play = useCallback((cand) => {
    if (!cand.previewUrl) return;
    const audio = getAudio();
    // Clicking the one that is playing stops it — the same control, both ways.
    if (playingUuid === cand.uuid) {
      audio.pause();
      setPlayingUuid(null);
      return;
    }
    audio.pause();
    audio.src = cand.previewUrl;
    audio.onended = () => setPlayingUuid(null);
    audio.onerror = () => setPlayingUuid(null);
    audio.play().then(() => setPlayingUuid(cand.uuid)).catch(() => setPlayingUuid(null));
  }, [getAudio, playingUuid]);

  /** ⛔ The only operation-spending action on this screen. */
  const cast = useCallback(async (character, cand) => {
    setBusyUuid(cand.uuid);
    try {
      const r = await apiClient.post(
        `/litgroups/${litGroupUuid}/casting/candidates/${cand.uuid}/commit`, {},
      );
      const out = r.data || {};
      if (out.ok) {
        setNotes((n) => ({
          ...n,
          [character.characterUuid]: `Cast — ${OPS_PER_COMMIT} operations spent`
            + (out.attached && out.attached !== 'attached' ? ` (attach: ${out.attached})` : '')
            + '.',
        }));
        if (onCast) onCast(character);
        await load();   // the character leaves the queue once it has a voice
      } else {
        // ⚠️ `stale` and `capped` arrive as 200s with a reason, because they are states of the world
        // to act on — and the two have different remedies.
        setNotes((n) => ({ ...n, [character.characterUuid]: out.message || 'Could not cast that voice.' }));
        if (out.status === 'stale') await load();
      }
    } catch (err) {
      setNotes((n) => ({
        ...n,
        [character.characterUuid]: err?.response?.data?.error || err.message || 'Could not cast that voice.',
      }));
    } finally {
      setBusyUuid(null);
    }
  }, [litGroupUuid, load, onCast]);

  /** Fresh auditions for one character. ⭐ Costs a design call and NO operations. */
  const resample = useCallback(async (character) => {
    setBusyUuid(character.characterUuid);
    setNotes((n) => ({ ...n, [character.characterUuid]: 'Auditioning again…' }));
    try {
      // ⭐ Announced BEFORE the POST, so the parent's progress strip is already subscribed when a
      // short run finishes — and so the operator sees something happen on the click itself.
      if (onRunStarted) onRunStarted(1);
      await apiClient.post(`/litgroups/${litGroupUuid}/casting/design-candidates`, {
        targetUuids: [character.characterUuid],
      });
      setNotes((n) => ({
        ...n,
        [character.characterUuid]: 'Auditioning again — the new takes appear when the run finishes.',
      }));
    } catch (err) {
      setNotes((n) => ({
        ...n,
        [character.characterUuid]: err?.response?.data?.error || err.message || 'Could not resample',
      }));
    } finally {
      setBusyUuid(null);
    }
  }, [litGroupUuid, onRunStarted]);

  /**
   * ⛔ THROW AUDITIONS AWAY. The exit from a queue you did not mean to generate.
   *
   * A run auditions whoever it was given, and an already-voiced character is eligible — so a filter
   * left wide auditions the whole cast and the queue fills with work already done. Without this the
   * only ways out were to cast (2 operations, replacing a voice chosen by ear) or to scroll past it
   * for good.
   *
   * ⭐ Free. The previews were paid for by the design call; operations are spent on commit alone.
   */
  /** Several at once — the retired set is cleared as one decision, not one row at a time. */
  const discardMany = useCallback(async (uuids) => {
    if (!uuids || !uuids.length) return;
    setBusyUuid('many');
    try {
      const r = await apiClient.post(`/litgroups/${litGroupUuid}/casting/candidates/discard`, {
        characterUuids: uuids,
      });
      if (r.data && r.data.ok) await load();
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Could not discard those takes');
    } finally {
      setBusyUuid(null);
    }
  }, [litGroupUuid, load]);

  const discard = useCallback(async (character) => {
    setBusyUuid(character.characterUuid);
    try {
      const r = await apiClient.post(`/litgroups/${litGroupUuid}/casting/candidates/discard`, {
        characterUuids: [character.characterUuid],
      });
      if (r.data && r.data.ok) await load();
      else setNotes((n) => ({ ...n, [character.characterUuid]: 'Could not discard those auditions.' }));
    } catch (err) {
      setNotes((n) => ({
        ...n,
        [character.characterUuid]: err?.response?.data?.error || err.message || 'Could not discard',
      }));
    } finally {
      setBusyUuid(null);
    }
  }, [litGroupUuid, load]);

  /**
   * ⭐ START OVER. One decision, not N.
   *
   * ⛔ After a run made with the wrong filter — or with prompts worth rewriting before trying again
   * — the queue has to be emptied, and discarding eighty characters one button at a time is how a
   * cleanup gets abandoned half-done.
   *
   * ⚠️ Confirmed, unlike the per-character discard, because this one is not glanceable: the
   * operator cannot see from the button how much it will throw away. Nothing is bought back by it,
   * but regenerating eighty characters' auditions is a real wait.
   */
  const [clearing, setClearing] = useState(false);
  const [confirmClear, setConfirmClear] = useState(false);
  const clearAll = useCallback(async () => {
    setClearing(true);
    try {
      const r = await apiClient.post(`/litgroups/${litGroupUuid}/casting/candidates/discard`, { all: true });
      if (r.data && r.data.ok) { setConfirmClear(false); setNotes({}); await load(); }
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Could not clear the auditions');
      setConfirmClear(false);
    } finally {
      setClearing(false);
    }
  }, [litGroupUuid, load]);

  const characters = useMemo(() => (data?.characters || []), [data]);
  /** Merged-away characters whose takes are still on file — listed, not cast. */
  const retired = useMemo(() => (data?.retired || []), [data]);

  if (loading && !data) return <Loader active inline="centered" size="small" content="Loading auditions…" />;
  if (error) return <Message negative size="small"><Icon name="warning sign" /> {String(error)}</Message>;

  if (!characters.length && !retired.length) {
    // ⚠️ During a run the queue is legitimately empty for minutes. Saying "no auditions waiting"
    // then reads as a run that never started, which is the exact confusion the progress strip
    // above exists to remove — so this message defers to it.
    if (runActive) {
      return (
        <Message size="small" icon>
          <Icon name="circle notched" loading />
          <Message.Content>
            <Message.Header>Auditioning…</Message.Header>
            {runMessage || 'Three takes per character. They appear here as the run finishes.'}
          </Message.Content>
        </Message>
      );
    }
    return (
      <Message size="small" info>
        <Icon name="microphone slash" />
        No auditions waiting. ⭐ Use <strong>Audition N voices</strong> on the Cast list, with the Book
        Filter set, to generate three takes for each new and unvoiced character — that step spends no
        voice operations.
      </Message>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '.75em', marginBottom: '.75em' }}>
        <Header as="h4" style={{ margin: 0 }}>
          {characters.length} character{characters.length === 1 ? '' : 's'} awaiting a voice
        </Header>
        {/* ⚠️ The ration, stated once at the top: everything here is free until a Cast click. */}
        <span style={{ fontSize: '12px', opacity: 0.75 }}>
          Listening is free · casting costs {OPS_PER_COMMIT} operations each
        </span>
        <Button size="mini" basic icon labelPosition="left" onClick={load} loading={loading} style={{ marginLeft: 'auto' }}>
          <Icon name="refresh" /> Refresh
        </Button>
        <Button size="mini" basic compact disabled={clearing || !!busyUuid} onClick={() => setConfirmClear(true)}>
          <Icon name="trash alternate outline" /> Clear all
        </Button>
      </div>

      {/*
        ⛔ WHERE THE MISSING ONES WENT. A merge tombstones a character, and its takes stay behind;
        casting one would buy a voice for a tombstone whose lines now belong to somebody else. So
        they are out of the queue — but SAID, because a queue that silently shrinks from 59 to 53
        is indistinguishable from takes going missing.
      */}
      {!!retired.length && (
        <Message size="tiny" info style={{ marginBottom: '.75em' }}>
          <Icon name="compress" />
          <strong>
            {retired.length} character{retired.length === 1 ? '' : 's'} left the queue
          </strong>
          <div style={{ marginTop: '.3em' }}>
            {retired.map((x) => (
              <div key={x.characterUuid}>
                · {x.characterName}
                {/*
                  ⚠️ THREE WAYS OFF THE QUEUE, and they are not the same story.
                    · merged away   — somebody absorbed them; their lines belong to the canonical
                    · no lines left — nobody absorbed THEM; their dialogue was reassigned elsewhere
                    · dismissed     — marked as not a real character
                  "cheerful penguins" is the second kind: a Character minted from a mis-parsed
                  embedded quote, whose text was joined back into the Narrator's line. Calling that
                  "merged" would send the operator looking for a merge that never happened.
                */}
                {x.mergedInto
                  ? <> → <strong>{x.mergedInto}</strong></>
                  : x.liveLines === 0
                    ? <span style={{ opacity: 0.85 }}> — no lines left; their dialogue was reassigned</span>
                    : ' (dismissed)'}
                {x.mergedIntoVoice
                  ? <span style={{ opacity: 0.8 }}> — already voiced, nothing to cast</span>
                  : x.mergedInto
                    ? <span style={{ opacity: 0.8 }}> — ⚠️ which has no voice yet; audition THEM</span>
                    : null}
              </div>
            ))}
          </div>
          <Button
            size="mini" basic compact style={{ marginTop: '.4em' }}
            disabled={!!busyUuid}
            onClick={() => discardMany(retired.map((x) => x.characterUuid))}
          >
            <Icon name="trash alternate outline" /> Discard their takes (free)
          </Button>
        </Message>
      )}

      {characters.map((ch) => {
        const stale = ch.candidates.some((c) => c.status === 'stale');
        const sample = ch.candidates.find((c) => c.sampleText);
        /**
         * ⭐ THREE STATES, because the middle one is now the common case for a walk-on: their own
         * short line FIRST, then stock text to make up the length ('own-then-stock').
         *
         * ⚠️ Treating that as "their own words" would overstate it badly — four words of theirs in
         * front of 440 characters of stock is a stock audition with a flourish. So it says how
         * much is actually theirs and lets the operator weigh it.
         */
        const stock = sample && sample.sampleSource === 'stock';
        const mostlyStock = sample && sample.sampleSource === 'own-then-stock';
        return (
          <Segment key={ch.characterUuid} color={stale ? 'orange' : 'teal'} style={{ marginBottom: '.75em' }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '.5em', flexWrap: 'wrap' }}>
              <strong style={{ fontSize: '15px' }}>{ch.characterName}</strong>
              {ch.tier && <Label size="mini" basic>{ch.tier}</Label>}
              {/*
                ⭐ THE NAME THE VOICE WILL CARRY, said before the money is spent rather than
                discovered afterwards in the ElevenLabs account. With 660 slots and three takes
                per character, "what will this be called" is not a small question.
              */}
              <Popup
                size="tiny"
                content={`Casting a take creates an ElevenLabs voice named "${ch.characterName} · cast". The preview files are named after the character too.`}
                trigger={(
                  <span style={{ fontSize: '11.5px', opacity: 0.6 }}>
                    → {ch.characterName} · cast
                  </span>
                )}
              />
              {/*
                ⛔ THE ONE THING THAT CHANGES WHAT A CAST CLICK MEANS HERE. This character already
                has a voice somebody chose; casting a take REPLACES it. Unlabelled, this row is
                indistinguishable from a first casting — which is how a queue full of already-done
                work looked like work to do.
              */}
              {/*
                ⛔ THE ONE THAT COST SIX AUDITIONS. `Doc` held a voice; `docpod` and `Doctor` were
                separate, unmerged, correctly-eligible nodes of one line each — so the merge pass
                had missed them and each burned three takes on a speaker already cast.

                ⚠️ A SUSPICION, not a rule, and it never blocks a cast: containment is the same
                heuristic Guided Merge uses, and "Glendyl" vs "Glendyl's father" are different
                people. An ALIAS match is different — that is the merge record itself — so it says
                so in stronger words.
              */}
              {!!(ch.possibleAliasOf || []).length && (
                <Popup
                  size="tiny"
                  content={(ch.possibleAliasOf.some((x) => x.alias)
                    ? 'An alias of this character is recorded on a character that already has a voice. They are the same speaker: merge instead of casting.'
                    : 'The name resembles a character that already has a voice. If they are the same speaker, merge them in the Green Room instead — casting here spends 2 operations on a duplicate.')}
                  trigger={(
                    <Label size="mini" color={ch.possibleAliasOf.some((x) => x.alias) ? 'red' : 'yellow'}>
                      <Icon name="exclamation triangle" />
                      {ch.possibleAliasOf.some((x) => x.alias) ? 'alias of' : 'looks like'}
                      {' '}{ch.possibleAliasOf.map((x) => x.name).join(', ')} — already voiced
                    </Label>
                  )}
                />
              )}
              {ch.alreadyVoiced && (
                <Popup
                  size="tiny"
                  content="This character already has a voice. Casting one of these takes replaces it — and the replacement costs 2 operations. If you did not mean to audition them, discard."
                  trigger={(
                    <Label size="mini" color="orange">
                      <Icon name="exclamation triangle" /> already voiced
                    </Label>
                  )}
                />
              )}
              <span style={{ fontSize: '12px', opacity: 0.75 }}>
                {ch.lines} line{ch.lines === 1 ? '' : 's'}
                {ch.gender ? ` · ${ch.gender}` : ''}{ch.age ? ` · ${ch.age}` : ''}
              </span>
              <div style={{ marginLeft: 'auto', display: 'flex', gap: '.35em' }}>
                <Button
                  size="mini" basic compact
                  loading={busyUuid === ch.characterUuid}
                  disabled={!!busyUuid}
                  onClick={() => resample(ch)}
                >
                  <Icon name="redo" /> Resample (free)
                </Button>
                {/* ⭐ No confirmation: nothing is bought, nothing is unlinked, and the takes can be
                    regenerated for free. A dialog here would be friction over a reversible act. */}
                <Button
                  size="mini" basic compact
                  disabled={!!busyUuid}
                  onClick={() => discard(ch)}
                  title="Drop these takes — free. The character stays in the cast list and can be auditioned again."
                >
                  <Icon name="trash alternate outline" /> Discard
                </Button>
              </div>
            </div>

            {/* ⭐ WHAT THEY ARE READING, because a voice judged on the character's own words is a
                stronger decision than one judged on a stock paragraph — and the operator cannot tell
                which they just heard without being told. */}
            {sample && (
              <div style={{ fontSize: '12.5px', opacity: 0.85, margin: '.4em 0' }}>
                {stock ? (
                  <Popup
                    size="tiny"
                    content="This character has no usable line at all, so they are reading stock text. A voice judged this way is a weaker decision."
                    trigger={<span><Icon name="warning sign" color="orange" /> reading stock text</span>}
                  />
                ) : mostlyStock ? (
                  <Popup
                    size="tiny"
                    content={`Nothing they say reaches the ~100 characters Voice Design needs, so their own line leads and stock text makes up the length. ${sample.sampleOwnChars || 0} of ${sample.sampleText?.length || 0} characters are theirs — judge the opening, and weigh the rest lightly.`}
                    trigger={(
                      <span>
                        <Icon name="warning sign" color="yellow" />
                        {' '}their line, then stock text
                        {sample.sampleOwnChars
                          ? <span style={{ opacity: 0.7 }}> ({sample.sampleOwnChars} chars theirs)</span>
                          : null}
                      </span>
                    )}
                  />
                ) : (
                  <span><Icon name="quote left" size="small" /> their own words</span>
                )}
                <span style={{ marginLeft: '.5em', fontStyle: 'italic' }}>
                  {String(sample.sampleText).slice(0, 150)}
                  {String(sample.sampleText).length > 150 ? '…' : ''}
                </span>
              </div>
            )}

            {stale && (
              <Message size="tiny" warning style={{ margin: '.4em 0' }}>
                <Icon name="clock outline" />
                ElevenLabs no longer accepts these takes. Resample to hear fresh ones — no operations
                were spent.
              </Message>
            )}

            <div style={{ display: 'flex', gap: '.5em', flexWrap: 'wrap', marginTop: '.5em' }}>
              {ch.candidates.map((c, i) => (
                <Segment key={c.uuid} compact style={{ padding: '.5em .7em', margin: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '.5em' }}>
                    <Button
                      size="mini" circular icon
                      color={playingUuid === c.uuid ? 'teal' : undefined}
                      disabled={!c.previewUrl}
                      onClick={() => play(c)}
                      title={c.previewUrl ? 'Listen' : 'Preview unavailable'}
                    >
                      <Icon name={playingUuid === c.uuid ? 'pause' : 'play'} />
                    </Button>
                    <span style={{ fontSize: '12px', opacity: 0.8 }}>
                      Take {i + 1}{fmtSecs(c.durationSecs) ? ` · ${fmtSecs(c.durationSecs)}` : ''}
                      {/* ⚠️ Which model designed it — blank means the provider chose and we do not
                          know which. Shown because two takes can differ for this reason alone. */}
                      {c.designModelId ? ` · ${String(c.designModelId).replace(/^eleven_/, '')}` : ''}
                    </span>
                    <Button
                      size="mini" basic compact icon
                      disabled={!c.previewUrl}
                      onClick={() => downloadTake(ch, c, i)}
                      title={`Save as "${ch.characterName} - take ${i + 1}.mp3"`}
                    >
                      <Icon name="download" />
                    </Button>
                  </div>
                  <Button
                    size="mini" primary fluid style={{ marginTop: '.45em' }}
                    loading={busyUuid === c.uuid}
                    disabled={!!busyUuid || c.status === 'stale'}
                    onClick={() => cast(ch, c)}
                  >
                    Cast · {OPS_PER_COMMIT} ops
                  </Button>
                </Segment>
              ))}
            </div>

            {/* The recipe. Collapsed, because it is the thing you read when a voice is WRONG. */}
            <div style={{ marginTop: '.5em' }}>
              <Button
                size="mini" basic compact
                onClick={() => setOpenPrompt((o) => ({ ...o, [ch.characterUuid]: !o[ch.characterUuid] }))}
              >
                <Icon name={openPrompt[ch.characterUuid] ? 'caret down' : 'caret right'} />
                {openPrompt[ch.characterUuid] ? 'Hide' : 'Show'} the prompt
              </Button>
              {openPrompt[ch.characterUuid] && (
                <div style={{ fontSize: '12.5px', opacity: 0.9, marginTop: '.4em', whiteSpace: 'pre-wrap' }}>
                  {ch.candidates[0]?.prompt || '(no prompt recorded)'}
                </div>
              )}
            </div>

            {notes[ch.characterUuid] && (
              <div style={{ fontSize: '12.5px', marginTop: '.5em' }}>
                <Icon name="info circle" /> {notes[ch.characterUuid]}
              </div>
            )}
          </Segment>
        );
      })}

      {/*
        ⛔ IN-APP, NEVER window.confirm — standing policy in this repo.

        ⭐ AND IT SAYS WHAT IS NOT LOST, because that is the actual question: discarding removes a
        character from THIS QUEUE, not from the cast. They stay in the cast list, still new and
        unvoiced, and auditioning them again is one button away.
      */}
      <ConfirmModal
        open={confirmClear}
        title="Clear every audition?"
        icon="trash alternate outline"
        danger={false}
        confirmLabel={clearing ? 'Clearing…' : 'Clear them all'}
        onCancel={() => setConfirmClear(false)}
        onConfirm={clearAll}
      >
        <div style={{ fontSize: '14px', lineHeight: 1.5 }}>
          <p>
            Throws away every take waiting here — {characters.length} character
            {characters.length === 1 ? '' : 's'}. ⭐ No voice operations were spent on them and none
            are spent now.
          </p>
          <p style={{ margin: '.5em 0', opacity: 0.85 }}>
            ⚠️ The characters are <strong>not</strong> removed from the cast. They go back to being
            new &amp; unvoiced, and <strong>Audition N voices</strong> on the Cast list generates
            fresh takes whenever you want them — useful when the prompts themselves are what needs
            changing.
          </p>
        </div>
      </ConfirmModal>
    </div>
  );
};

export default VoiceCandidateReview;
